import { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  serverTimestamp,
  doc,
  updateDoc,
  getDoc,
  increment,
  deleteField,
} from "firebase/firestore";
import { db, auth } from "../firebase";
import { useAuth } from "../context/AuthContext";
import TweetForm from "./TweetForm";
import TweetListCareer from "./TweetListCareer";
import ConfirmModal from "./ConfirmModal";
import UserContextMenu from "./UserContextMenu";
import Swal from "sweetalert2";
import { containsBadWords } from "../utils/contentFilter";
import { notifyError } from "../utils/errorHandler";

function getCareerEmoji(carrera) {
  if (!carrera) return "";

  const lower = carrera.toLowerCase();

  if (lower.includes("medicina")) return "👨‍⚕️🏥⚕️";
  if (lower.includes("enfermería")) return "🩺💉🩹";
  if (lower.includes("odontología")) return "👩🏼‍⚕️🦷🩻";
  if (lower.includes("psicología")) return "👨🏿‍🏭🦿⚙️";
  if (lower.includes("veterinaria")) return "🐾🐕‍🦺";
  if (lower.includes("bioanálisis")) return "🩸🔬🧬";
  if (lower.includes("farmacia")) return "🥼💊";
  if (lower.includes("ingeniería electromecánica")) return "👨🏿‍🏭🦿⚙️";
  if (lower.includes("ingeniería industrial")) return "👷🏾‍♀️🏭🦺";
  if (lower.includes("ingeniería civil")) return "👷🏽🏗️🛠️";
  if (
    lower.includes("ingeniería en software") ||
    lower.includes("ingeniería en ciberseguridad") ||
    lower.includes("hardware y redes")
  )
    return "👨🏽‍💻🌐📡";
  if (lower.includes("administración")) return "📊💼";
  if (lower.includes("economía")) return "📈💰";
  if (lower.includes("contabilidad")) return "🧾📉";
  if (lower.includes("mercadeo")) return "📣🛍️";
  if (lower.includes("finanzas")) return "💹💳";
  if (lower.includes("educación")) return "📚👩‍🏫";
  if (lower.includes("lengua")) return "🗣️📖";
  if (lower.includes("literatura")) return "📘🖋️";
  if (lower.includes("matemáticas")) return "➗📐";
  if (lower.includes("primaria")) return "👶📒";
  if (lower.includes("secundaria")) return "👦📗";
  if (lower.includes("derecho")) return "⚖️👨‍⚖️";
  if (lower.includes("química")) return "🧪⚗️";
  if (lower.includes("biología")) return "🦠🔬";
  if (lower.includes("arquitectura")) return "🏛️📏";

  return "";
}

function CareerView() {
  const { currentUser } = useAuth();
  const [tweet, setTweet] = useState("");
  const [media, setMedia] = useState([]);
  const [tweets, setTweets] = useState([]);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tweetToDelete, setTweetToDelete] = useState(null);

  // Menú contextual de usuario
  const [contextMenuPos, setContextMenuPos] = useState({ top: 0, left: 0 });
  const [selectedUser, setSelectedUser] = useState(null);
  const [contextMenuOpen, setContextMenuOpen] = useState(false);

  useEffect(() => {
    if (!currentUser?.carrera) return;

    const carreraTweetsRef = collection(db, `tweetsByCareer/${currentUser.carrera}/tweets`);
    const q = query(carreraTweetsRef, where("deleted", "==", false));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedTweets = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      fetchedTweets.sort((a, b) => b.date?.seconds - a.date?.seconds);
      setTweets(fetchedTweets);
    });

    return () => unsubscribe();
  }, [currentUser]);

  const handleTweet = async (mediaUrls) => {
  const trimmedTweet = tweet.trim();

    if (currentUser?.status === "pending") {
      return Swal.fire({
        icon: "info",
        title: "Cuenta pendiente",
        text: "Podrás publicar en tu carrera una vez que tu cuenta sea verificada.",
        confirmButtonColor: "#3b82f6",
      });
    }

    if (!trimmedTweet)
      return Swal.fire("Oops", "El tweet está vacío.", "warning");

    if (containsBadWords(trimmedTweet)) {
      return Swal.fire("Lenguaje no permitido", "Tu publicación contiene palabras ofensivas o inapropiadas. Por favor, modifícala antes de tweetear.", "error");
    }

    const newTweet = {
      content: tweet,
      userId: currentUser.uid,
      userName: currentUser.displayName,
      nickname: currentUser.nickname || "",
      carrera: currentUser.carrera || "",
      email: currentUser.email || "",
      role: currentUser.role || "",
      media: mediaUrls,
      date: serverTimestamp(),
      likes: { count: 0, users: {} },
      replies: {},
      deleted: false,
    };

    try {
      const carreraRef = collection(db, `tweetsByCareer/${currentUser.carrera}/tweets`);
      const tweetRef = await addDoc(carreraRef, newTweet); // 👈 Captura la referencia del doc

      const idToken = await auth.currentUser.getIdToken();
      await fetch(`${backendUrl}/api/send-career-notification`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          authorId: currentUser.uid,
          career: currentUser.carrera,
          title: `${currentUser.nickname || currentUser.displayName} publicó un tweet en tu carrera`,
          body: tweet,
          tweetId: tweetRef.id
        }),
      });

      setTweet("");
      setMedia([]);
    } catch (err) {
      console.error("Error publicando tweet:", err);
      notifyError("No se pudo publicar el tweet.");
    }
  };

  const handleLike = async (tweetId, currentLikes) => {
    if (!currentUser) return;

    const tweetRef = doc(db, `tweetsByCareer/${currentUser.carrera}/tweets`, tweetId);
    const hasLiked = currentLikes.users?.[currentUser.uid];

    try {
      if (hasLiked) {
        await updateDoc(tweetRef, {
          [`likes.users.${currentUser.uid}`]: deleteField(),
          "likes.count": increment(-1),
        });
      } else {
        await updateDoc(tweetRef, {
          [`likes.users.${currentUser.uid}`]: { name: currentUser.displayName },
          "likes.count": increment(1),
        });
      }
    } catch (err) {
      console.error("Error actualizando like:", err);
      notifyError("No se pudo actualizar el like.");
    }
  };

  const handleReply = async (tweetId, text) => {
    if (!currentUser || !text.trim()) return;

    const tweetRef = doc(db, `tweetsByCareer/${currentUser.carrera}/tweets`, tweetId);
    const replyId = doc(collection(db, "dummy")).id;

    const replyData = {
      id: replyId,
      userId: currentUser.uid,
      userName: currentUser.displayName,
      nickname: currentUser.nickname || "",
      content: text,
      date: serverTimestamp(),
    };

    try {
      await updateDoc(tweetRef, {
        [`replies.${replyId}`]: replyData,
      });

      // 🔔 Notificación al autor del tweet
      const tweetSnap = await getDoc(tweetRef);
      const tweetData = tweetSnap.data();
      const authorId = tweetData?.userId;

      if (authorId && authorId !== currentUser.uid) {
        const idToken = await auth.currentUser.getIdToken();
        await fetch(`${backendUrl}/api/send-reply-notification`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            recipientId: authorId,
            title: `${currentUser.nickname || currentUser.displayName} respondió tu tweet`,
            body: text,
          }),
        });
      }

    } catch (err) {
      console.error("Error agregando reply:", err);
      notifyError("No se pudo enviar la respuesta.");
    }
  };

  const handleDeleteReply = async (tweetId, replyId) => {
    if (!currentUser) return;

    const tweetRef = doc(db, `tweetsByCareer/${currentUser.carrera}/tweets`, tweetId);

    try {
      await updateDoc(tweetRef, {
        [`replies.${replyId}`]: deleteField(),
      });
    } catch (err) {
      console.error("Error eliminando reply:", err);
      notifyError("No se pudo eliminar la respuesta.");
    }
  };

  const confirmDeleteTweet = (tweetId) => {
    setTweetToDelete(tweetId);
    setIsModalOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    if (!tweetToDelete) return;

    try {
      const tweetRef = doc(db, `tweetsByCareer/${currentUser.carrera}/tweets`, tweetToDelete);
      await updateDoc(tweetRef, { deleted: true });
      setIsModalOpen(false);
      setTweetToDelete(null);
      setTweets(tweets.filter(t => t.id !== tweetToDelete));
    } catch (err) {
      console.error("Error eliminando tweet:", err);
      notifyError("No se pudo eliminar el tweet.");
    }
  };

  const handleCancelDelete = () => {
    setIsModalOpen(false);
    setTweetToDelete(null);
  };

  const openUserContextMenu = (e, user) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    setContextMenuPos({
      top: rect.top + rect.height / 2,
      left: rect.left + rect.width + 8,
    });
    setSelectedUser(user);
    setContextMenuOpen(true);
  };

  return (
    <div className="max-w-2xl mx-auto p-4 relative">
      <h2 className="text-2xl font-bold mb-4">
        Tweets de tu carrera (
        <span className="text-green-400 font-extrabold">
          {currentUser?.carrera || "..."}
        </span>{" "}
        {getCareerEmoji(currentUser?.carrera)}
        )
      </h2>

      {currentUser?.status === "pending" ? (
        <div className="mb-4 bg-gray-100 dark:bg-gray-700 border border-dashed border-gray-300 dark:border-gray-500 rounded-xl px-4 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
          🔒 Podrás publicar en tu carrera una vez que tu cuenta sea verificada.
        </div>
      ) : (
        <TweetForm
          tweet={tweet}
          setTweet={setTweet}
          handleTweet={handleTweet}
          media={media}
          setMedia={setMedia}
        />
      )}

      <TweetListCareer
        tweets={tweets}
        handleLike={handleLike}
        handleDelete={confirmDeleteTweet}
        handleReply={handleReply}
        handleDeleteReply={handleDeleteReply}
        career={currentUser.carrera}
        onUserClick={openUserContextMenu}
      />

      <ConfirmModal
        isOpen={isModalOpen}
        onConfirm={handleDeleteConfirmed}
        onCancel={handleCancelDelete}
        message="¿Estás seguro que quieres eliminar este tweet?"
      />
    </div>
  );
}

export default CareerView;
